import type { FootprintSize } from './towers'
import type { GridPosition, MapDefinition, TileType } from './types'

function cellKey(x: number, y: number): string {
  return `${x},${y}`
}

function tileTypeFromChar(char: string): TileType | null {
  if (char === 'G') return 'grass'
  if (char === 'P') return 'path'
  return null
}

export type FootprintIssue = 'OUT_OF_BOUNDS' | 'INVALID_TERRAIN' | 'OCCUPIED'

export type FootprintCheck =
  | { ok: true; cells: GridPosition[] }
  | { ok: false; reason: FootprintIssue; cells: GridPosition[] }

/**
 * Logical board for one map.
 * Terrain (G/P) and occupancy (towers) stay separate.
 */
export class Grid {
  readonly width: number
  readonly height: number
  private readonly tiles: TileType[][]
  private readonly occupied = new Set<string>()
  private readonly occupants = new Map<string, string[]>()
  private readonly cellOwner = new Map<string, string>()

  constructor(definition: MapDefinition) {
    this.width = definition.width
    this.height = definition.height
    this.tiles = definition.rows.map((row) =>
      [...row].map((char) => tileTypeFromChar(char) ?? 'grass')
    )
  }

  isInBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height
  }

  getTile(x: number, y: number): TileType | null {
    if (!this.isInBounds(x, y)) return null
    return this.tiles[y][x]
  }

  isOccupied(position: GridPosition): boolean {
    return this.occupied.has(cellKey(position.x, position.y))
  }

  /** Resolve which tower occupies a cell, if any. */
  towerIdAt(position: GridPosition): string | null {
    return this.cellOwner.get(cellKey(position.x, position.y)) ?? null
  }

  isBuildable(x: number, y: number): boolean {
    return this.isInBounds(x, y) && this.getTile(x, y) === 'grass' && !this.isOccupied({ x, y })
  }

  canPlaceFootprint(anchor: GridPosition, footprint: FootprintSize): FootprintCheck {
    const cells: GridPosition[] = []
    let outOfBounds = false
    let invalidTerrain = false
    let occupied = false

    for (let dy = 0; dy < footprint.height; dy += 1) {
      for (let dx = 0; dx < footprint.width; dx += 1) {
        const cell = { x: anchor.x + dx, y: anchor.y + dy }
        cells.push(cell)
        if (!this.isInBounds(cell.x, cell.y)) {
          outOfBounds = true
          continue
        }
        if (this.getTile(cell.x, cell.y) !== 'grass') {
          invalidTerrain = true
        }
        if (this.isOccupied(cell)) {
          occupied = true
        }
      }
    }

    if (outOfBounds) return { ok: false, reason: 'OUT_OF_BOUNDS', cells }
    if (invalidTerrain) return { ok: false, reason: 'INVALID_TERRAIN', cells }
    if (occupied) return { ok: false, reason: 'OCCUPIED', cells }
    return { ok: true, cells }
  }

  occupy(towerId: string, cells: GridPosition[]): void {
    this.release(towerId)
    const keys = cells.map((cell) => cellKey(cell.x, cell.y))
    for (const key of keys) {
      this.occupied.add(key)
      this.cellOwner.set(key, towerId)
    }
    this.occupants.set(towerId, keys)
  }

  release(towerId: string): void {
    const keys = this.occupants.get(towerId)
    if (!keys) return
    for (const key of keys) {
      this.occupied.delete(key)
      this.cellOwner.delete(key)
    }
    this.occupants.delete(towerId)
  }
}
